import ContactForm from './ContactForm';

interface UseCasesProps {
  onOpenContactForm: () => void;
}

export default function UseCases({ onOpenContactForm }: UseCasesProps) {
  const cases = [
    {
      icon: 'ri-medicine-bottle-line',
      title: '学会・新薬情報を調査したい',
      color: 'from-orange-500 to-orange-600',
      items: [
        '新薬と従来の薬剤を比較している文献を収集したい',
        '注目の新薬に関する副作用情報を収集したい',
        '今までにない新薬の機序について調査したい'
      ],
      image: 'https://readdy.ai/api/search-image?query=Modern%20pharmaceutical%20research%20laboratory%20with%20organized%20medical%20bottles%20and%20clean%20white%20surfaces%2C%20professional%20healthcare%20setting%20with%20natural%20lighting%2C%20minimalist%20scientific%20workspace%20with%20subtle%20green%20accents%2C%20organized%20medication%20research%20environment&width=600&height=400&seq=usecase-002&orientation=landscape'
    },
    {
      icon: 'ri-user-search-line',
      title: '先生情報を調査したい',
      color: 'from-teal-500 to-teal-600',
      items: [
        '学会で発表される先生の業績を調査したい',
        '新しく担当する先生の情報をいち早く知りたい',
        'その領域における論文発表の多い先生を調査したい'
      ],
      image: 'https://readdy.ai/api/search-image?query=Professional%20medical%20researcher%20analyzing%20academic%20papers%20and%20publications%20in%20a%20bright%20modern%20office%2C%20clean%20white%20workspace%20with%20organized%20documents%2C%20soft%20natural%20lighting%2C%20minimalist%20healthcare%20research%20environment%20with%20subtle%20green%20accents&width=600&height=400&seq=usecase-001&orientation=landscape'
    },
    {
      icon: 'ri-book-open-line',
      title: '雑誌情報を調査したい',
      color: 'from-blue-500 to-blue-600',
      items: [
        '雑誌に掲載された注目トピックの関連論文が欲しい',
        '雑誌に掲載された薬剤の研究について、同系統の他剤での研究状況を知りたい'
      ],
      image: 'https://readdy.ai/api/search-image?query=Clean%20organized%20medical%20journal%20library%20with%20neatly%20arranged%20academic%20publications%2C%20bright%20white%20shelving%20system%2C%20professional%20healthcare%20documentation%20center%20with%20soft%20lighting%2C%20minimalist%20scholarly%20environment%20with%20subtle%20green%20accents&width=600&height=400&seq=usecase-003&orientation=landscape'
    }
  ];

  return (
    <section id="usecases" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-800 mb-4">例えばこのような場合にご利用ください</h2>
          <p className="text-lg text-gray-600">様々なニーズに対応した文献検索サービス</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {cases.map((useCase, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 border border-gray-100">
              <div className="relative h-48 w-full overflow-hidden">
                <img 
                  src={useCase.image} 
                  alt={useCase.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className={`absolute inset-0 bg-gradient-to-br ${useCase.color} opacity-20`}></div>
              </div>
              <div className="p-8">
                <div className={`inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br ${useCase.color} rounded-full mb-4 shadow-lg`}>
                  <i className={`${useCase.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-4">{useCase.title}</h3>
                <ul className="space-y-3">
                  {useCase.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-gray-700">
                      <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5 flex-shrink-0"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mb-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <button
              onClick={onOpenContactForm}
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#eb4b3f] to-[#f0945b] text-white text-base font-semibold rounded-full hover:from-[#d43d31] hover:to-[#e08549] transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-right-circle-line mr-2 text-xl"></i>
              キーワード検索を利用する
            </button>
          </div>
        </div>

        <div className="bg-gradient-to-br from-[#eb4b3f]/10 to-[#f0945b]/10 rounded-3xl p-8 md:p-12 text-center border border-[#eb4b3f]/20">
          <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            インフォレスタでは、お客様の細かいご希望に合わせた情報をご提供いたします
          </h3>
        </div>

        <div className="mt-12 bg-white rounded-2xl p-8 border-l-4 border-[#eb4b3f] shadow-md">
          <p className="text-base text-gray-700 mb-2">
            定期的に文献調査が必要な方には、オプションサービス
            <span className="text-2xl font-bold text-[#eb4b3f] mx-2">『SDI検索』</span>
            がおすすめです。
          </p>
          <p className="text-sm text-gray-600 mb-1">ご指定の期間毎に、最新の文献情報を調査してお届けいたします。</p>
          <p className="text-sm text-gray-600">毎回のご依頼にかかるコストとお手間の削減に、ぜひご検討ください。</p>
        </div>
      </div>
    </section>
  );
}